/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React from 'react';
import {
  type ContentStackBlogtypeGrid,
  type ContentStackLink,
  type ContentStackBlogPostSummary,
} from '@/features/contentstack/interface';
import { CMSBlogGrid } from '@/features/contentstack/components/CMSBlogGrid/CMSBlogGrid';
import { View } from '@az/starc-ui';
import { useLabels } from '@/hooks/useLabels';
import { VIDEO_FALLBACK_IMAGE } from '../../constants';

type RelatedBlogsProps = {
  relatedBlogContent: ContentStackBlogPostSummary[];
};

const labelMap = {
  headline: 'label_RelatedBlogs_Headline',
  ctaTitle: 'label_RelatedBlogs_CTATitle',
};

export const RelatedBlogs: React.FC<RelatedBlogsProps> = ({ relatedBlogContent = [] }) => {
  const { headline, ctaTitle } = useLabels(labelMap);

  if (relatedBlogContent.length === 0) {
    return null;
  }

  const blogGridContent: ContentStackBlogtypeGrid = {
    title: headline,
    headline: headline,
    blog_content: relatedBlogContent.map((blog) => {
      const isImage = blog.feature_content?.media_type === 'Image';
      const image = isImage
        ? blog.feature_content?.image?.image_url
        : blog.feature_content?.video?.poster_image;
      const imageUrl = image || VIDEO_FALLBACK_IMAGE;
      return {
        heading: blog.post_heading?.heading ?? '',
        description: blog.seo?.meta_description ?? '',
        image: {
          image_url: imageUrl,
          image_alt_text: blog.feature_content?.image?.image_alt_text,
          attributes: blog.feature_content?.image?.attributes,
        },
        link: blog.url ?? '#',
        link_cta: {
          title: ctaTitle,
          href: blog.url ?? '#',
        } as ContentStackLink,
      };
    }),
  };

  const reviewers = relatedBlogContent.map((blog) => {
    return {
      blogReviewer: blog.reviewer?.[0],
      date: blog.reviewed_date,
    };
  });

  return (
    <View data-testid="related-blogs-container">
      <CMSBlogGrid content={blogGridContent} reviewers={reviewers} />
    </View>
  );
};

export default RelatedBlogs;
